/**
 * 발주처(B2B) 모드 — 광해 포털 등 회사 포털에서 넘어온 계정이 React 화면(엑셀 자동화·공사 서류)을 쓸 때,
 * 서비스 제공사(콘엑스) 표시를 모두 걷어내기 위한 컨텍스트.
 *
 * 판정은 /tenant/bootstrap/ 응답에 company 와 me 가 있는가로 한다(역할 무관: 공단 관리자·감독자·업체 모두).
 * 켜지면: 탭 제목·파비콘·헤더·푸터·상담 위젯·이용권 구매 유도가 바뀌고, 엑셀 경로 밖은 포털로 돌려보낸다.
 * 소속이 없으면 null — 일반 콘엑스 화면은 아무것도 달라지지 않는다.
 */
import React, { createContext, useContext } from "react";

export type TenantInfo = {
  companyName: string;   // 발주처 회사명(공단) 또는 업체 회사명(업체 계정)
  serviceName: string;   // 포털 서비스명(예: 광해방지사업 D&B 지원 서비스)
  orgName: string;       // 발주처 회사명(항상 공단)
  name: string;          // 로그인한 사람 이름
  role: string;          // owner/admin/member/partner
  isPartner: boolean;
};

const TenantContext = createContext<TenantInfo | null>(null);
export const TenantProvider = TenantContext.Provider;
export const useTenant = (): TenantInfo | null => useContext(TenantContext);

// 렌더 밖(알림 문구 등)에서 읽을 수 있게 모듈 변수에도 둔다.
let current: TenantInfo | null = null;
export const setTenantInfo = (t: TenantInfo | null) => { current = t; };
export const getTenantInfo = (): TenantInfo | null => current;
export const isTenantMode = (): boolean => !!current;
/** 발주처 모드로 쓰던 흔적(지난 로드의 localStorage). 세션이 끊겨 tenant 정보가 아직 없을 때 "콘엑스로 돌려보내지 않기" 판단용. */
/** 흔적 키는 빌드마다 따로 — 데모(/komir/)와 콘엑스가 같은 도메인(dev.ddusul.com)이라 localStorage 를 공유한다.
 *  같은 키를 쓰면 데모를 써 본 브라우저가 콘엑스 메인에서도 포털로 끌려간다(10-26). index.html 도 같은 규칙. */
const STORAGE_KEY = process.env.PUBLIC_URL ? "tenant_mode_title" : "conax_tenant_mode_title";
export const hadTenantMode = (): boolean => { try { return !!localStorage.getItem(STORAGE_KEY); } catch (e) { return false; } };
/** 실패 안내문의 "어디에 말하라" — 콘엑스 회원은 고객센터, 발주처 포털 사용자는 발주처 담당자(채널톡·고객센터가 없다). */
export const supportLabel = (): string => (current || hadTenantMode()) ? "발주처 담당자" : "고객센터";
/** 발주처 모드에서 로그인이 끊겼을 때: 콘엑스 로그인/메인이 아니라 포털로 되돌린다. 돌려보냈으면 true. */
export const backToPortalIfTenant = (): boolean => {
  if (!(current || hadTenantMode())) return false;
  window.location.replace(portalHomeUrl());
  return true;
};

/** 다음 로드 때 index.html 이 콘엑스 제목·상담 위젯을 띄우지 않도록 흔적을 남긴다. 소속이 아니면 흔적을 지우고 제목을 되돌린다. */
export const rememberTenantMode = (t: TenantInfo | null) => {
  try {
    if (t) localStorage.setItem(STORAGE_KEY, t.isPartner ? (t.companyName || "업체 포털") : (t.serviceName || t.orgName || "포털"));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (e) { /* 저장 불가 환경 — 무시 */ }
  if (!t) {
    const w = window as any;
    if (w.__TENANT_MODE__ && w.__TENANT_PREV_TITLE__) { document.title = w.__TENANT_PREV_TITLE__; }
    w.__TENANT_MODE__ = false;
  }
};

/** 부트스트랩 응답 → TenantInfo. 소속이 없으면 null. */
export const tenantFromBootstrap = (boot: any): TenantInfo | null => {
  const company = boot?.data?.company;
  const me = boot?.data?.me;
  if (!company || !me) return null;
  const isPartner = me.role === "partner";
  const orgName = company.name || company.display_name || "";
  return {
    companyName: isPartner ? (me.company_name || me.name || "") : orgName,
    serviceName: company.service_name || orgName,
    orgName,
    name: me.name || "",
    role: me.role || "",
    isPartner,
  };
};

/** 포털로 돌아가는 주소(앵귤러 포털의 /company). */
export const portalHomeUrl = (): string => `${process.env.REACT_APP_COST_URL || "/"}company`;
/** 안내문에 쓰는 발주처 호칭 — 업체 계정에는 발주처 이름을 노출하지 않는다. */
export const ownerLabel = (t: TenantInfo | null): string => (!t || t.isPartner ? "발주처" : (t.orgName || "발주처"));

/** 발주처 모드에서 열어 두는 React 경로. 그 밖은 포털로 돌려보낸다. */
export const TENANT_ALLOWED_PREFIXES = ["/excel"];
export const isTenantAllowedPath = (pathname: string): boolean =>
  TENANT_ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));

/** 중립 파비콘(서류 모양) — 서비스 제공사 아이콘을 대신한다. */
export const NEUTRAL_FAVICON =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="7" fill="#17365D"/><path d="M10 8h8l4 4v12H10z" fill="#fff"/><path d="M18 8v4h4" fill="#C9D6EA"/><path d="M12.5 17h7M12.5 20h7M12.5 23h5" stroke="#17365D" stroke-width="1.4" stroke-linecap="round"/></svg>'
  );

/** 발주처 모드 부수효과: 탭 제목·파비콘·상담 위젯. 되돌리는 함수를 돌려준다. */
export const applyTenantChrome = (t: TenantInfo): (() => void) => {
  const prevTitle = document.title;
  // 업체 계정에는 발주처 이름도 보이지 않게: 탭 제목은 업체 자기 회사명
  document.title = t.isPartner ? (t.companyName || "업체 포털") : (t.serviceName || t.orgName || "포털");
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]'));
  const prevHrefs = links.map((l) => l.href);
  links.forEach((l) => { l.href = NEUTRAL_FAVICON; });
  if (!links.length) {
    const l = document.createElement("link"); l.rel = "icon"; l.href = NEUTRAL_FAVICON; l.setAttribute("data-tenant", "1"); document.head.appendChild(l);
  }
  // 상담 위젯(채널톡)은 서비스 제공사 창구라 발주처 모드에서는 띄우지 않는다.
  // 첫 로드에서는 index.html 이 이미 부팅을 걸어 둔 뒤라 shutdown 이 boot 보다 먼저 불릴 수 있다 → 1분간 반복 + CSS 로도 가린다.
  (window as any).__TENANT_MODE__ = true;
  const style = document.createElement("style"); style.setAttribute("data-tenant", "1");
  style.textContent = "#ch-plugin, #ch-plugin-launcher, [id^='ch-plugin']{display:none !important}";
  document.head.appendChild(style);
  const started = Date.now();
  const stop = () => {
    const ch = (window as any).ChannelIO;
    if (typeof ch === "function") { try { ch("shutdown"); } catch (e) { /* ignore */ } }
  };
  stop();
  const timer = window.setInterval(() => { stop(); if (Date.now() - started > 60000) window.clearInterval(timer); }, 1000);
  return () => {
    window.clearInterval(timer);
    if (style.parentNode) style.parentNode.removeChild(style);
    (window as any).__TENANT_MODE__ = false;
    document.title = prevTitle;
    links.forEach((l, i) => { l.href = prevHrefs[i]; });
    const added = document.querySelector('link[data-tenant="1"]'); if (added && added.parentNode) added.parentNode.removeChild(added);
  };
};
